import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  useEventCallback,
} from '@mui/material';
import { ScrollToTop } from '../../../common/table/helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import CancelIcon from '@lmt/unicorn-shared/app/icons/cancel.svg?react';
import { IconButton } from '../../../common/ui-kit/IconButton';
import { snakeCase } from 'lodash';
import { Input } from '../../../common/ui-kit/inputs/Input';
import { AdditionalVisitDto } from '@lmt/shared-types';
import styles from './AdditionalVisitTable.module.scss';
import { TimePicker } from '../../../common/ui-kit/inputs/date-time/TimePicker';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { VisitType } from '../../../calendar/visit/common/types';
import { UniLink } from '../../../common/ui-kit/buttons/UniLink';
import { HL7SettingsFormProps } from './types';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useTranslation } from 'react-i18next';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import { useFieldArray } from 'react-hook-form';

const tableKeys: { [dictionary: string]: string } = {
  type: 'type',
  visitType: 'visitType',
  startTime: 'startTime',
  visitLength: 'visitLength',
  options: 'options',
};

const columnKeys = [
  tableKeys.type,
  tableKeys.visitType,
  tableKeys.startTime,
  tableKeys.visitLength,
];

const columnStyles = {
  [tableKeys.type]: {
    minWidth: '140px',
    width: '20%',
  },
  [tableKeys.visitType]: {
    minWidth: '35px',
    width: '32%',
  },
  [tableKeys.startTime]: {
    minWidth: '120px',
    width: '120px',
  },
  [tableKeys.visitLength]: {
    minWidth: '120px',
    width: '25%',
  },
  [tableKeys.options]: {
    minWidth: '32px',
    width: '32px',
  },
};

export const AdditionalVisitTable: FC<HL7SettingsFormProps> = React.memo(
  function AdditionalVisitTable(props) {
    const i18next = useScopedTranslation(
      'tenant_settings.hl7.incoming.visit_creating',
    );
    const i18nextGlobal = useTranslation();
    const nameof = nameofFactory<AdditionalVisitDto>();
    const additionalVisitsFormArray = useFieldArray({
      control: props.form.control,
      name: 'additionalVisits',
    });

    const { watch, setValue, getValues, formState } = props.form;
    const [additionalVisits, defaultVisitTypeId] = watch([
      'additionalVisits',
      'defaultVisitTypeId',
    ]);

    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [additionalVisits]);

    const visitTypes = useActiveVisitTypes();
    const translatedVisitTypes: VisitType[] = useMemo(
      () =>
        visitTypes.map((type) => ({
          id: type.id,
          label: i18nextGlobal.t(type.value as any),
          specificStartTimeInSecondsSinceMidnight:
            type.specificStartTimeInSecondsSinceMidnight,
          defaultDurationInSeconds: type.defaultDurationInSeconds,
        })),
      [visitTypes],
    );

    const onRemove = useEventCallback((index: number) => {
      if (!additionalVisits) {
        return;
      }
     
      additionalVisitsFormArray.remove(index);
    });

    const onEdit = useEventCallback(
      (index: number, visit: AdditionalVisitDto) => {
        if (!additionalVisits) {
          return;
        }
        setValue(
          'additionalVisits',
          additionalVisits.map((d, i) => (i === index ? visit : d)),
          validateAndDirty,
        );
      },
    );

    const onAdd = useEventCallback(() => {
      const defaultVisitType = translatedVisitTypes.find(
        (x) => x.id === defaultVisitTypeId,
      );
      const newVisit: AdditionalVisitDto = {
        type: '',
        visitTypeId: defaultVisitType?.id ?? 0,
        visitStartTimeInSecondsSinceMidnight:
          defaultVisitType?.specificStartTimeInSecondsSinceMidnight ??
          getValues('visitStartTimeInSecondsSinceMidnight') ??
          61200,
        visitLengthInSeconds:
          defaultVisitType?.defaultDurationInSeconds ?? 3600,
      };
      additionalVisitsFormArray.append(newVisit);
    });

    const header = (
      <div className={styles.tableHeader}>
        <div className={styles.tableTitle}>{i18next.t('title')}</div>
        <div>
          <UniLink
            title={i18next.t('add_button')}
            onClick={onAdd}
            testId="add-button"
          />
        </div>
      </div>
    );

    return (
      <div className={styles.container} data-test-id="visit-creating-table">
        {header}
        <TableContainer
          ref={tableRef}
          className={styles.tableContainer}
          component={Paper}
        >
          <Table stickyHeader className={styles.table}>
            <TableHead>
              <TableRow>
                {columnKeys.map((columnKey) => (
                  <TableCell
                    key={`${columnKey}`}
                    style={columnStyles[columnKey]}
                  >
                    <div className={styles.header}>
                      {i18next.t(`${snakeCase(columnKey)}` as any)}
                    </div>
                  </TableCell>
                ))}
                <TableCell
                  className={styles.header}
                  key={tableKeys.options}
                  style={columnStyles[tableKeys.options]}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(additionalVisitsFormArray.fields ?? []).map((data, index) => (
                <TableRow
                  key={data.id}
                  hover
                  style={{ minWidth: '1000px' }}
                  data-test-id="table-row"
                >
                  <TableCell
                    style={columnStyles[tableKeys.type]}
                    data-test-id="condition-field"
                  >
                    <div className={styles.cell}>
                      <Input
                        {...props.form.register(
                          `additionalVisits.${index}.type`,
                        )}
                        errorText={
                          (
                            formState.errors?.additionalVisits?.[index]
                              ?.type as any
                          )?.message
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell style={columnStyles[tableKeys.visitType]}>
                    <div className={styles.cell}>
                      <DropDownInput
                        variant={'formInput'}
                        useIdFunctionAsValue={true}
                        options={translatedVisitTypes}
                        value={data.visitTypeId}
                        idFunction={'id'}
                        getOptionLabel={'label'}
                        required={true}
                        onValueChanged={(value) =>
                          onEdit(index, {
                            ...data,
                            visitTypeId: value?.id,
                            visitStartTimeInSecondsSinceMidnight:
                              value.specificStartTimeInSecondsSinceMidnight ??
                              data.visitStartTimeInSecondsSinceMidnight,
                            visitLengthInSeconds:
                              value.defaultDurationInSeconds ??
                              data.visitLengthInSeconds,
                          })
                        }
                        testId="visit-type-dropdown"
                      />
                    </div>
                  </TableCell>
                  <TableCell style={columnStyles[tableKeys.startTime]}>
                    <div className={styles.cell}>
                      <TimePicker
                        variant="formInput"
                        timeInMills={
                          data.visitStartTimeInSecondsSinceMidnight
                            ? data.visitStartTimeInSecondsSinceMidnight * 1000
                            : data.visitStartTimeInSecondsSinceMidnight
                        }
                        error={
                          !!formState.errors?.additionalVisits?.[index]
                            ?.visitStartTimeInSecondsSinceMidnight
                        }
                        errorText={
                          (
                            formState.errors?.additionalVisits?.[index]
                              ?.visitStartTimeInSecondsSinceMidnight as any
                          )?.message
                        }
                        onTimeChanged={(value) =>
                          onEdit(index, {
                            ...data,
                            visitStartTimeInSecondsSinceMidnight: value
                              ? value / 1000
                              : (value as any),
                          })
                        }
                        testId="start-time-field"
                      />
                    </div>
                  </TableCell>
                  <TableCell style={columnStyles[tableKeys.visitLength]}>
                    <div
                      className={styles.cell}
                      data-test-id="visit-length-field"
                    >
                      <Input
                        variant="formInput"
                        value={data.visitLengthInSeconds / 3600}
                        type="number"
                        step="any"
                        onChange={(value) =>
                          onEdit(index, {
                            ...data,
                            visitLengthInSeconds:
                              value.currentTarget.valueAsNumber * 3600,
                          })
                        }
                        errorText={
                          (
                            formState.errors?.additionalVisits?.[index]
                              ?.visitLengthInSeconds as any
                          )?.message
                        }
                      />
                    </div>
                  </TableCell>

                  <TableCell
                    className={styles.optionCell}
                    onClick={(event: any) => event.stopPropagation()}
                    style={columnStyles[tableKeys.options]}
                  >
                    <div className={styles.cell}>
                      <IconButton
                        color="grey"
                        iconComponent={
                          <CancelIcon className={styles.optionIcon} />
                        }
                        onClick={() => onRemove(index)}
                        testId="cross-button"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  },
);
